import React, { useEffect, useState } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Divider,
  Stack,
  Chip,
} from "@mui/material"

// APIs
import {
  fetch_wallet_detail,
  create_wallet_order,
  verify_wallet_payment,
} from "../../services/apis.js"

const WalletManagement = () => {
  const [balance, setBalance] = useState(0)
  const [amount, setAmount] = useState("")
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)

  // ----------------------------
  // Load wallet on mount
  // ----------------------------
  useEffect(() => {
    loadWallet()
  }, [])

  const loadWallet = () => {
    fetch_wallet_detail()
      .then((res) => {
        setBalance(Number(res.balance || 0))
        setTransactions(res.transactions || [])
      })
      .catch((err) => {
        console.error("Failed to load wallet", err)
      })
  }

  // ----------------------------
  // Add money + Razorpay flow
  // ----------------------------
  const handleAddMoney = () => {
    if (!amount || Number(amount) <= 0) return

    setLoading(true)

    create_wallet_order({ amount })
      .then((order) => {
        if (!window.Razorpay) {
          alert("Razorpay SDK not loaded")
          setLoading(false)
          return
        }

        const options = {
          key: order.key,
          amount: order.amount, // in paise
          currency: "INR",
          order_id: order.order_id,
          name: "MakeLoanEazy",
          description: "Wallet Top-up",

          handler: function (response) {
            verify_wallet_payment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            })
              .then(() => {
                loadWallet()
                setAmount("")
              })
              .catch((err) => {
                console.error("Payment verification failed", err)
                alert("Payment verification failed")
              })
              .finally(() => {
                setLoading(false)
              })
          },

          modal: {
            ondismiss: function () {
              setLoading(false)
            },
          },

          theme: {
            color: "#4f46e5",
          },
        }

        const rzp = new window.Razorpay(options)
        rzp.open()
      })
      .catch((err) => {
        console.error("Order creation failed", err)
        alert("Unable to initiate payment")
        setLoading(false)
      })
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        display: "flex",
        justifyContent: "center",
        paddingTop: 6,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        {/* Wallet Balance */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            background:
              "linear-gradient(135deg, #4f46e5, #6366f1)",
            color: "#fff",
          }}
        >
          <CardContent>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Wallet Balance
            </Typography>
            <Typography variant="h4" fontWeight="bold" mt={1}>
              ₹ {balance.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Used automatically during advisor sessions
            </Typography>
          </CardContent>
        </Card>

        {/* Add Money */}
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={600} mb={2}>
              Add Money
            </Typography>

            <Stack direction="row" spacing={1} mb={2}>
              {[500, 1000, 2000].map((val) => (
                <Chip
                  key={val}
                  label={`₹${val}`}
                  clickable
                  onClick={() => setAmount(val)}
                />
              ))}
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Button
                variant="contained"
                sx={{ borderRadius: 2 }}
                onClick={handleAddMoney}
                disabled={loading || Number(amount) < 1}
              >
                {loading ? "Processing..." : "Add"}
              </Button>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={1}
            >
              Minimum amount ₹1
            </Typography>
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={600} mb={2}>
              Recent Transactions
            </Typography>

            {transactions.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No transactions yet
              </Typography>
            )}

            {transactions.map((tx, index) => (
              <React.Fragment key={tx.id}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {tx.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(tx.created_at).toLocaleString()}
                    </Typography>
                  </Box>

                  <Typography
                    fontWeight={600}
                    color={
                      tx.type === "credit"
                        ? "success.main"
                        : "error.main"
                    }
                  >
                    {tx.type === "credit" ? "+" : "-"}₹{tx.amount}
                  </Typography>
                </Box>

                {index !== transactions.length - 1 && (
                  <Divider sx={{ my: 1 }} />
                )}
              </React.Fragment>
            ))}
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export default WalletManagement
